import type { AppState } from '../types';
import { DEFAULT_DIRIGEANT_TAUX_CHARGES } from './dirigeantDefaults';
import { generateId } from './utils';

/**
 * Returns a minimal empty AppState used as fallback when the Supabase
 * template cannot be fetched. All preset data (My Notary) lives in the
 * `app_templates` table — see supabase-seed.sql.
 */
export function createEmptyAppState(): AppState {
  return {
    meta: {
      tresoInitiale: 0,
      societeNom: '',
      societeActivite: '',
      societeMarches: '',
      societeCreation: new Date().toISOString().slice(0, 10),
      formeJuridique: 'SASU',
      exercice1Duree: 12,
      exercice2Duree: 12,
      exercice3Duree: 12,
      logoUrl: '',
      primaryColor: '#313131',
      tauxIS: 25,
      tauxISReduit: 15,
      seuilISReduit: 42500,
      tauxPFU: 30,
      defaultTauxSalarie: 42,
      defaultTauxDirigeant: 45,
      caScenarioPct: 100,
    },
    revenus: [],
    charges: {
      capex: [],
      marketing: [],
      outils: [],
      tech: [],
      banque: [],
      legal: [],
      locaux: [],
      vehicules: [],
      freelances: [],
      autres: [],
    },
    investissements: [],
    salaireCategories: [],
    salaries: [],
    dirigeants: [
      {
        id: generateId(),
        nom: 'Dirigeant',
        notes: '',
        modeSaisie: 'brut' as const,
        regime: 'custom',
        customTaux: DEFAULT_DIRIGEANT_TAUX_CHARGES,
        remuMode: true,
        remuUniform: 0,
        remuMensuels: Array(36).fill(0),
        remuReels: Array<number | null>(36).fill(null),
        dividendes: [0, 0, 0],
      },
    ],
    fiscalite: {
      tauxReduitPME: true,
      cfe: [0, 0, 0],
      autresTaxes: [],
    },
    reel: {
      ca: Array(36).fill(null) as (number | null)[],
      charges: Array(36).fill(null) as (number | null)[],
      masseSalariale: Array(36).fill(null) as (number | null)[],
      treso: Array(36).fill(null) as (number | null)[],
    },
    ui: {
      periode: 'all',
      synthesePeriode: 'all',
      dashboardMonthFrom: null,
      dashboardMonthTo: null,
    },
    salairesBudget: Array(36).fill(0),
    salairesBudgetMode: 'fixed',
    salairesBudgetPct: 0,
    notairesConfig: {
      enabled: false,
      panierMoyen: 110,
      capaciteParNotaire: 150,
      salaireNotaire: 5000,
      notairesInitiaux: 1,
      seuilRecrutementPct: 80,
      delaiRecrutement: 1,
      coutPartenairePct: 30,
      seuilHeadOps: 4,
      salaireHeadOps: 6000,
      ratioAssistant: 6,
      salaireAssistant: 3500,
      sousTraitanceReels: Array<number | null>(36).fill(null),
    },
    reelOverrides: {
      ca: Array<number | null>(36).fill(null),
      chargesOpex: Array<number | null>(36).fill(null),
      masseSalariale: Array<number | null>(36).fill(null),
      capex: Array<number | null>(36).fill(null),
      sousTraitance: Array<number | null>(36).fill(null),
      commandesPartenaires: Array<number | null>(36).fill(null),
      coutParCommandePartenaire: Array<number | null>(36).fill(null),
      ebe: Array<number | null>(36).fill(null),
      resultatAvantIS: Array<number | null>(36).fill(null),
      is: Array<number | null>(36).fill(null),
      cfe: Array<number | null>(36).fill(null),
      margeNette: Array<number | null>(36).fill(null),
      flux: Array<number | null>(36).fill(null),
      tresorerie: Array<number | null>(36).fill(null),
    },
  };
}

// Keep alias for backward compatibility with reducer STATE_RESET action
export { createEmptyAppState as createDefaultAppState };
